<template>
    <div>
        <component :is="'script'" type="application/ld+json" v-html="businessJsonLd" />
        <transition name="page-fade">
            <div v-if="pageReady">
                <div class="" :style="{ backgroundColor: homePageData?.navbar_color }">
                    <div class="d-flex justify-content-center">
                        <img v-if="homePageData?.banner_image" :src="`${$API_ASSETS}${homePageData.banner_image.id}?width=1400&quality=80`"
                            class="img-fluid" alt="globe" fetchpriority="high" />
                    </div>
                    <div class="d-flex align-items-center justify-content-center pt-3">
                        <div class="d-flex align-items-center text-start text-white px-5">
                            <i class="fa-solid fa-star d-none d-sm-block"></i>
                            <h1 class="text-uppercase h2 home-text mx-2 text-center">
                              Improv Shows Every Friday & Saturday
                            </h1>
                            <i class="fa-solid fa-star d-none d-sm-block"></i>
                        </div>
                    </div>
                    <div class="d-flex align-items-center justify-content-center pt-5 pb-5">
                        <a :href="`${$TICKETS_URL}`">
                            <div class="btn ms-2 text-uppercase mb-5"
                                style="background-color:white; color:#E35B4B; border-radius: 1; min-width: 180px;"
                                type="button">Buy Tickets</div>
                        </a>
                    </div>
                </div>
                <UpcomingShows :showsData="showsData" :featuredWorkshops="featuredWorkshops" />
                <PageSections :sectionId="1" />
                <Footer />
            </div>
        </transition>
    </div>
</template>

<script>
import Footer from '../components/Footer.vue'
import { useHead } from '@vueuse/head';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { mapState } from 'vuex';
import UpcomingShows from '../components/UpcomingShows.vue';
import PageSections from '@/components/PageSections.vue';

export default {
    name: 'Home',
    setup() {
        const route = useRoute();
        useHead({
            title: route.meta.title || 'Atlas Improv',
            meta: [
                { name: 'description', content: route.meta.description || '' }
            ]
        });
        return {};
    },
    components: {
        Footer, UpcomingShows, PageSections
    },
    data() {
        return {
            homePageData: null,
            showsData: [],
            featuredWorkshops: [],
            tickerData: null,
            pageReady: false,
        };
    },
    computed: {
        ...mapState({
            count: state => state.count
        }),
        businessJsonLd() {
            return JSON.stringify({
                "@context": "https://schema.org",
                "@type": "PerformingArtsTheater",
                "name": "Atlas Improv Co.",
                "url": "https://atlasimprov.com",
                "logo": "https://atlasimprov.com/social_card_1200x630.png",
                "image": "https://atlasimprov.com/social_card_1200x630.png",
                "description": "Madison's only dedicated improv stage. Live, character-driven comedy every Friday and Saturday at 7 & 9 PM, plus year-round classes for all skill levels.",
                "telephone": "+1-608-216-5533",
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "609 E Washington Ave",
                    "addressLocality": "Madison",
                    "addressRegion": "WI",
                    "postalCode": "53703",
                    "addressCountry": "US"
                },
                "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": 43.0758,
                    "longitude": -89.3776
                },
                "openingHoursSpecification": [
                    {
                        "@type": "OpeningHoursSpecification",
                        "dayOfWeek": ["Friday", "Saturday"],
                        "opens": "19:00",
                        "closes": "22:00"
                    }
                ],
                "sameAs": [
                    "https://www.facebook.com/atlasimprov",
                    "https://www.instagram.com/atlasimprov"
                ]
            });
        }
    },
    methods: {
        async fetchPageData() {
            const url = this.$API_GRAPHQL;
            const headers = {
                'Authorization': `Bearer Pm4Nd5_e46HaK2xtkPcla2PMBzbk9AQf`,
                'Content-Type': 'application/json'
            };
            try {
                const [homeRes, showsRes] = await Promise.all([
                    axios.post(url, {
                        query: `
                          query {
                            home_page {
                              name
                              banner_image { id }
                              navbar_color
                              logo { id }
                            }
                            ticker {
                              text
                              useLink
                              hyperlink
                            }
                          }
                        `
                    }, { headers }),
                    axios.post(url, {
                        query: `
                          query {
                            show(filter: { featured: { _eq: true } }) {
                              id
                              name
                              date_text
                              family_friendly
                              description
                              fw_link
                              learn_more
                              pay_what_you_can
                              banner_image { id }
                            }
                            featuredClasses: class {
                              id
                              name
                              meets_text
                              long_desc
                              link
                              is_featured
                              core_req
                              is_free
                              banner_image { id }
                            }
                          }
                        `
                    }, { headers }),
                ]);

                this.homePageData = homeRes.data.data.home_page;
                this.tickerData = homeRes.data.data.ticker;
                this.showsData = showsRes.data.data.show || [];
                this.featuredWorkshops = (showsRes.data.data.featuredClasses || []).filter(c => c.is_featured === true);

                const imageUrls = [
                    `${this.$API_ASSETS}${this.homePageData.banner_image.id}?width=1400&quality=80`,
                    ...this.showsData.map(s => `${this.$API_ASSETS}${s.banner_image.id}?width=800&quality=80`)
                ];

                await Promise.all(imageUrls.map(src => new Promise(resolve => {
                    const img = new Image();
                    img.onload = img.onerror = resolve;
                    img.src = src;
                })));

                this.pageReady = true;
            } catch (error) {
                console.error('Error fetching page data:', error);
                this.pageReady = true;
            }
        }
    },
    created() {
        this.fetchPageData();
    },
};
</script>

<style scoped>
.page-fade-enter-active {
    transition: opacity 0.6s ease;
}
.page-fade-enter-from {
    opacity: 0;
}
</style>
